/**
 * PageSkeleton
 * ────────────────────────────────────────────────────────────
 * Dark, athlete-first placeholder shown between route swaps
 * and while data fetches. Sized to visually approximate the
 * most common Flythouse layout (header pill → hero block →
 * meta row → content rail) so the eye latches onto real UI
 * shapes instead of a bar of loading text.
 *
 * All animation is driven by the CSS in `index.html` (critical,
 * inlined) so the skeleton paints on the first frame even
 * before the main stylesheet hydrates. Zero JS dependencies.
 *
 * Variants:
 *   • `profile` (default) — hero + meta + stat rail (used for
 *     AthleteNetworkPage / share pages)
 *   • `feed` — vertical list of thumbnail cards (Homepage /
 *     Stories / Collections)
 */
const PageSkeleton = ({ variant = 'profile' }) => {
  if (variant === 'feed') {
    return (
      <div
        className="min-h-screen bg-black text-white page-fade-in"
        data-testid="page-skeleton-feed"
        aria-busy="true"
        aria-label="Loading content"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-24 space-y-5">
          <div className="flyt-skeleton h-8 w-40" />
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="space-y-3">
              <div className="flyt-skeleton aspect-[16/9] w-full" />
              <div className="flyt-skeleton h-4 w-3/4" />
              <div className="flyt-skeleton h-3 w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // profile — matches AthleteNetworkPage top region so the swap
  // between skeleton → real page feels like the same layout
  // gently coming into focus rather than a full re-shuffle.
  return (
    <div
      className="min-h-screen bg-black text-white page-fade-in"
      data-testid="page-skeleton-profile"
      aria-busy="true"
      aria-label="Loading profile"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-24">
        {/* School pill */}
        <div className="flex justify-end mb-4">
          <div className="flyt-skeleton h-7 w-40 rounded-full" />
        </div>
        {/* Avatar + name block */}
        <div className="flex items-center gap-4 mb-5">
          <div className="flyt-skeleton w-20 h-20 rounded-2xl shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="flyt-skeleton h-3 w-24" />
            <div className="flyt-skeleton h-6 w-48" />
            <div className="flyt-skeleton h-3 w-32" />
          </div>
        </div>
        {/* Action row */}
        <div className="flex gap-2 mb-5">
          <div className="flyt-skeleton h-9 w-20 rounded-full" />
          <div className="flyt-skeleton h-9 w-24 rounded-full" />
          <div className="flyt-skeleton h-9 w-20 rounded-full" />
        </div>
        {/* Meta chips row */}
        <div className="flex flex-wrap gap-2 mb-4">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flyt-skeleton h-6 w-14 rounded-full" />
          ))}
        </div>
        {/* Live game pill placeholder */}
        <div className="flyt-skeleton h-24 w-full rounded-3xl mb-6" />
        {/* Tab bar */}
        <div className="flex gap-6 border-b border-white/10 pb-3 mb-5">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flyt-skeleton h-4 w-14" />
          ))}
        </div>
        {/* Body copy */}
        <div className="space-y-3">
          <div className="flyt-skeleton h-4 w-full" />
          <div className="flyt-skeleton h-4 w-11/12" />
          <div className="flyt-skeleton h-4 w-4/5" />
        </div>
      </div>
    </div>
  );
};

export default PageSkeleton;
